import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static values = {
    handle: String,
    item: String,
    pointer: { type: Boolean, default: false },
    step: { type: Number, default: 10 }
  }

  connect() {
    this.handleClick = this.handleClick.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handlePointerDown = this.handlePointerDown.bind(this)
    this.handlePointerMove = this.handlePointerMove.bind(this)
    this.handlePointerUp = this.handlePointerUp.bind(this)
    this.handlePointerCancel = this.handlePointerCancel.bind(this)
    this.element.addEventListener("click", this.handleClick)
    this.element.addEventListener("keydown", this.handleKeydown)

    if (this.pointerValue) {
      this.connectPointerSorting()
    } else {
      this.connectSortable()
    }

    this.updatePositions(false)
  }

  disconnect() {
    if (this.pointerDragging) this.finishPointerDrag(false, true)
    this.sortable?.destroy()
    this.element.removeEventListener("click", this.handleClick)
    this.element.removeEventListener("keydown", this.handleKeydown)
    this.element.removeEventListener("pointerdown", this.handlePointerDown)
    this.element.removeEventListener("pointermove", this.handlePointerMove)
    this.element.removeEventListener("pointerup", this.handlePointerUp)
    this.element.removeEventListener("pointercancel", this.handlePointerCancel)
  }

  connectSortable() {
    this.sortable = Sortable.create(this.element, {
      animation: 150,
      draggable: this.itemValue,
      handle: this.handleValue,
      direction: "vertical",
      ghostClass: "knowledge-menu-sort-ghost",
      chosenClass: "knowledge-menu-sort-chosen",
      fallbackOnBody: true,
      invertSwap: true,
      swapThreshold: 0.65,
      invertedSwapThreshold: 0.65,
      scroll: true,
      scrollSensitivity: 80,
      scrollSpeed: 14,
      onChange: () => this.updatePositions(true),
      onUpdate: () => this.updatePositions(true),
      onEnd: () => this.updatePositions(true)
    })
  }

  connectPointerSorting() {
    this.element.addEventListener("pointerdown", this.handlePointerDown)
    this.element.addEventListener("pointermove", this.handlePointerMove)
    this.element.addEventListener("pointerup", this.handlePointerUp)
    this.element.addEventListener("pointercancel", this.handlePointerCancel)
  }

  handlePointerDown(event) {
    const handle = event.target.closest(this.handleValue)
    const item = handle?.closest(this.itemValue)
    if (!handle || !item || event.button !== 0) return

    this.pointerId = event.pointerId
    this.pointerHandle = handle
    this.pointerItem = item
    this.pointerStartY = event.clientY
    this.pointerOriginalNext = item.nextElementSibling
    this.pointerOriginalStyle = item.getAttribute("style")
    handle.setPointerCapture?.(event.pointerId)
  }

  handlePointerMove(event) {
    if (event.pointerId !== this.pointerId || !this.pointerItem) return
    if (!this.pointerDragging && Math.abs(event.clientY - this.pointerStartY) < 6) return

    event.preventDefault()
    this.startPointerDrag(event)
    this.positionPointerItem(event)
    this.movePointerItem(event.clientY)
    this.scrollForPointer(event.clientY)
  }

  handlePointerUp(event) {
    if (event.pointerId !== this.pointerId) return

    this.finishPointerDrag()
  }

  handlePointerCancel(event) {
    if (event.pointerId !== this.pointerId) return

    this.finishPointerDrag(false, true)
  }

  startPointerDrag(event) {
    if (this.pointerDragging) return

    const itemBounds = this.pointerItem.getBoundingClientRect()
    const summary = this.pointerItem.querySelector(":scope > summary")
    const summaryBounds = summary?.getBoundingClientRect() || itemBounds
    const label = summary?.querySelector("h2")?.textContent?.trim() || "space"

    this.pointerDragging = true
    this.pointerOffsetX = event.clientX - itemBounds.left
    this.pointerOffsetY = event.clientY - summaryBounds.top
    this.pointerPlaceholder = this.buildPointerPlaceholder(label, summaryBounds.height)
    this.pointerItem.before(this.pointerPlaceholder)
    this.pointerItem.classList.add("knowledge-menu-sort-chosen")
    this.pointerItem.classList.add("knowledge-menu-sort-dragging")
    this.pointerItem.style.left = `${itemBounds.left}px`
    this.pointerItem.style.top = `${summaryBounds.top}px`
    this.pointerItem.style.width = `${itemBounds.width}px`
    this.pointerHandle.setAttribute("aria-grabbed", "true")
    this.element.classList.add("knowledge-menu-sort-active")

    const status = document.getElementById("knowledge_menu_sort_status")
    if (status) status.textContent = `Moving ${label}. Release to place it.`
  }

  buildPointerPlaceholder(label, height) {
    const placeholder = document.createElement("div")
    const icon = document.createElement("i")
    const text = document.createElement("span")

    placeholder.className = "knowledge-menu-sort-placeholder card mb-3"
    placeholder.style.minHeight = `${Math.max(height, 52)}px`
    placeholder.setAttribute("aria-hidden", "true")
    icon.className = "fa-solid fa-arrow-down"
    text.textContent = `Place ${label} here`
    placeholder.append(icon, text)

    return placeholder
  }

  positionPointerItem(event) {
    this.pointerItem.style.left = `${event.clientX - this.pointerOffsetX}px`
    this.pointerItem.style.top = `${event.clientY - this.pointerOffsetY}px`
  }

  movePointerItem(pointerY) {
    const candidates = this.items.filter((item) => item !== this.pointerItem)
    const destination = candidates.find((item) => {
      const bounds = item.getBoundingClientRect()
      return pointerY < bounds.top + (bounds.height / 2)
    })

    if (destination) {
      if (this.pointerPlaceholder.nextElementSibling !== destination) {
        this.element.insertBefore(this.pointerPlaceholder, destination)
      }
    } else if (this.pointerPlaceholder.nextElementSibling) {
      this.element.append(this.pointerPlaceholder)
    }
  }

  scrollForPointer(pointerY) {
    const threshold = 72
    if (pointerY < threshold) {
      window.scrollBy(0, -16)
    } else if (pointerY > window.innerHeight - threshold) {
      window.scrollBy(0, 16)
    }
  }

  finishPointerDrag(announce = true, restore = false) {
    if (this.pointerDragging) {
      if (restore) {
        this.pointerPlaceholder?.remove()
        if (this.pointerOriginalNext?.parentElement === this.element) {
          this.element.insertBefore(this.pointerItem, this.pointerOriginalNext)
        } else {
          this.element.append(this.pointerItem)
        }
      } else {
        this.pointerPlaceholder?.replaceWith(this.pointerItem)
      }

      this.restorePointerItemStyle()
      this.updatePositions(announce)
    }

    this.pointerItem?.classList.remove("knowledge-menu-sort-chosen")
    this.pointerItem?.classList.remove("knowledge-menu-sort-dragging")
    this.pointerHandle?.removeAttribute("aria-grabbed")
    this.element.classList.remove("knowledge-menu-sort-active")

    if (this.pointerHandle?.hasPointerCapture?.(this.pointerId)) {
      this.pointerHandle.releasePointerCapture(this.pointerId)
    }

    this.pointerDragging = false
    this.pointerHandle = null
    this.pointerId = null
    this.pointerItem = null
    this.pointerPlaceholder = null
    this.pointerOriginalNext = null
    this.pointerOriginalStyle = null
    this.pointerOffsetX = null
    this.pointerOffsetY = null
  }

  restorePointerItemStyle() {
    if (this.pointerOriginalStyle === null) {
      this.pointerItem.removeAttribute("style")
    } else {
      this.pointerItem.setAttribute("style", this.pointerOriginalStyle)
    }
  }

  handleClick(event) {
    if (!event.target.closest(this.handleValue)) return

    event.preventDefault()
    event.stopPropagation()
  }

  handleKeydown(event) {
    if (event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return
    if (!event.target.closest(this.handleValue)) return
    if (!["ArrowUp", "ArrowDown"].includes(event.key)) return

    const item = event.target.closest(this.itemValue)
    const items = this.items
    const index = items.indexOf(item)
    const sibling = event.key === "ArrowUp" ? items[index - 1] : items[index + 1]
    if (!item || !sibling) return

    event.preventDefault()
    if (event.key === "ArrowUp") {
      this.element.insertBefore(item, sibling)
    } else {
      this.element.insertBefore(sibling, item)
    }
    this.updatePositions(true)
    event.target.focus()
  }

  updatePositions(announce) {
    this.items.forEach((item, index) => {
      const directInput = Array.from(item.children).find((child) => child.matches?.("[data-knowledge-menu-sort-position]"))
      const input = directInput || item.querySelector("[data-knowledge-menu-sort-position]")
      if (input) input.value = (index + 1) * this.stepValue
    })
    if (announce) {
      const status = document.getElementById("knowledge_menu_sort_status")
      if (status) status.textContent = "Order changed. Save the navigation menu to apply it."
    }
  }

  get items() {
    return Array.from(this.element.children).filter((child) => child.matches(this.itemValue))
  }
}
